import React, { useMemo } from 'react'
import { useFeature } from 'toggled'

import cn from '~/lib/cn'
import type { IProduct } from '~/types/products'
import { createAttributeComparator } from '~/utils/attribute-utils'
import { flags } from '~/utils/constants'
import { formatStockDisplay, getRealStock } from '~/utils/stock'

interface VariantAttributeMap {
  [attributeName: string]: string
}

interface VariantStockInfo {
  variant: IProduct
  stock: number
  restockDate: string
  incomingStock: number | null
  attributes: VariantAttributeMap
}

/**
 * Creates a comprehensive list of all variant combinations with their stock
 * @param variants - Array of all product variants
 * @returns Array of variant stock information
 */
export function getAllVariantsStockInfo(variants: IProduct[]): VariantStockInfo[] {
  if (!variants || variants.length === 0) {
    return []
  }

  return variants.map(variant => {
    const attributes: VariantAttributeMap = {}

    if (variant.attributes && variant.attributes.length > 0) {
      variant.attributes.forEach(attr => {
        attributes[attr.name] = attr.value
      })
    }

    return {
      variant,
      stock: variant.stock,
      restockDate: variant.stock === 0 ? variant.restockDate : null,
      incomingStock: variant.stock === 0 ? variant.incomingStock : null,
      attributes,
    }
  })
}

/**
 * Sorts variants by attributes in the order they're defined
 */
function sortVariantsByAttributes(variantsInfo: VariantStockInfo[], attributeNames: string[]): VariantStockInfo[] {
  const comparators = Object.fromEntries(attributeNames.map(name => [name, createAttributeComparator(name)]))

  return [...variantsInfo].sort((a, b) => {
    for (const attrName of attributeNames) {
      const aValue = a.attributes[attrName] || ''
      const bValue = b.attributes[attrName] || ''

      if (aValue !== bValue) {
        return comparators[attrName](aValue, bValue)
      }
    }

    return 0
  })
}

interface VariantsStockTableProps {
  variants: IProduct[]
  activeVariant?: IProduct
  className?: string
}

export const VariantsStockTable: React.FC<VariantsStockTableProps> = ({ variants, activeVariant, className }) => {
  const showProductStockFeature = useFeature(flags.SHOW_PRODUCT_STOCK)
  const handleStockFeature = useFeature(flags.HANDLE_STOCK)
  const stockCap = showProductStockFeature?.settings?.stockCap ?? null
  const handleStock = !!handleStockFeature

  const { sortedVariants, attributeNames, hasIncomingStock } = useMemo(() => {
    const variantsStockInfo = getAllVariantsStockInfo(variants)

    // Filter out variants without attributes (main product)
    const variantsWithAttributes = variantsStockInfo.filter(info => Object.keys(info.attributes).length > 0)

    if (variantsWithAttributes.length === 0) {
      return { sortedVariants: [], attributeNames: [] }
    }

    // Get attribute names in the order they appear in the first variant's attributes
    // This preserves the order defined in the product
    const firstVariantWithAttrs = variants.find(v => v.attributes && v.attributes.length > 0)
    const orderedAttrNames = firstVariantWithAttrs?.attributes?.map(attr => attr.name) || []

    // Add any missing attribute names (in case variants have different attributes)
    const allAttrNames = Array.from(new Set(variantsWithAttributes.flatMap(info => Object.keys(info.attributes))))
    const attrNames = [...orderedAttrNames, ...allAttrNames.filter(name => !orderedAttrNames.includes(name))]

    const sorted = sortVariantsByAttributes(variantsWithAttributes, attrNames)

    const hasIncomingStock = variantsWithAttributes.some(info => info.incomingStock != null && info.incomingStock > 0)

    return { sortedVariants: sorted, attributeNames: attrNames, hasIncomingStock }
  }, [variants])

  return (
    <div className={cn('w-full overflow-x-auto rounded-lg border text-xs', className)}>
      <table className="w-full">
        <thead className="border-b bg-gray-50">
          <tr className="font-semibold uppercase">
            {attributeNames.map(attrName => (
              <th key={attrName} className="whitespace-nowrap px-2 py-1.5 text-left">
                {attrName}
              </th>
            ))}
            <th className="whitespace-nowrap px-2 py-1.5 text-right">Stock</th>
            {hasIncomingStock && <th className="whitespace-nowrap px-2 py-1.5 text-right">En camino</th>}
          </tr>
        </thead>
        <tbody className="divide-y">
          {sortedVariants.map((info, index) => {
            const isActive = activeVariant?.id === info.variant.id
            const variant = info.variant
            const { realStock } = getRealStock(
              {
                stock: variant.stock,
                jumpFactor: variant.jumpFactor,
                minimumSalesQuantity: variant.minimumSalesQuantity,
                maximumSalesQuantity: variant.maximumSalesQuantity,
              },
              { handleStock },
            )
            const restockDate = realStock === 0 ? variant.restockDate : null

            return (
              <tr key={info.variant.sku || index} className={cn(isActive && 'bg-green-100 font-semibold')}>
                {attributeNames.map(attrName => (
                  <td key={`${info.variant.sku}-${attrName}`} className="whitespace-nowrap px-2 py-1.5">
                    {info.attributes[attrName] || '-'}
                  </td>
                ))}
                <td className="whitespace-nowrap px-2 py-1.5 text-right">
                  {formatStockDisplay(realStock, restockDate, stockCap)}
                </td>
                {hasIncomingStock && (
                  <td className="whitespace-nowrap px-2 py-1.5 text-right">
                    {realStock === 0 && variant.incomingStock ? variant.incomingStock : '-'}
                  </td>
                )}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export default VariantsStockTable
